import type { SortingState } from '@tanstack/react-table';

import type { ReportDateRange, ReportDateRangeValue } from './api/fetchReportData';
import { defaultReportId } from './mock/reports';
import { getReportTableConfig } from './reportTableConfigs';
import { getReportColumnById } from './reportTableTypes';
import { toTicketId } from './ticketFilter';

const VALID_DATE_RANGES: ReportDateRange[] = [
    'all-time',
    'today',
    'last-7-days',
    'last-30-days',
    'custom',
];

const DATE_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const REPORT_PAGE_LIMITS = [25, 50, 100, 250, 'all'] as const;
export type ReportPageLimit = (typeof REPORT_PAGE_LIMITS)[number];

export const DEFAULT_REPORT_PAGE = 1;
export const DEFAULT_REPORT_LIMIT: ReportPageLimit = 100;

export type ReportQueryParams = {
    report: string;
    view?: number;
    range: ReportDateRange;
    customStart?: string;
    customEnd?: string;
    q: string;
    sort: SortingState;
    page: number;
    limit: ReportPageLimit;
    /** `undefined` = all tickets. Explicit array (including empty) filters by product id. */
    tickets?: number[];
};

export const defaultReportQueryParams: ReportQueryParams = {
    report: defaultReportId,
    range: 'all-time',
    q: '',
    sort: [],
    page: DEFAULT_REPORT_PAGE,
    limit: DEFAULT_REPORT_LIMIT,
};

function parseViewId(value: string | null): number | undefined {
    if (!value) {
        return undefined;
    }

    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
        return undefined;
    }

    return parsed;
}

function parseDateParam(value: string | null): string | undefined {
    if (value && DATE_PARAM_PATTERN.test(value)) {
        return value;
    }

    return undefined;
}

function parseDateRange(value: string | null): ReportDateRange {
    if (value && VALID_DATE_RANGES.includes(value as ReportDateRange)) {
        return value as ReportDateRange;
    }
    return 'all-time';
}

export function toReportDateRangeValue(
    params: Pick<ReportQueryParams, 'range' | 'customStart' | 'customEnd'>,
): ReportDateRangeValue {
    if (params.range === 'custom' && params.customStart && params.customEnd) {
        return {
            range: 'custom',
            customStart: params.customStart,
            customEnd: params.customEnd,
        };
    }

    return { range: params.range };
}

function parseReportId(value: string | null): string {
    if (value) {
        return value;
    }
    return defaultReportId;
}

function parseSort(value: string | null, reportId: string): SortingState {
    if (!value) {
        return [];
    }

    const [columnId, direction] = value.split(':');
    if (!columnId || (direction !== 'asc' && direction !== 'desc')) {
        return [];
    }

    const config = getReportTableConfig(reportId);
    const column = config ? getReportColumnById(config.columns, columnId) : undefined;
    if (!column || column.sortable === false) {
        return [];
    }

    return [{ id: columnId, desc: direction === 'desc' }];
}

function parsePage(value: string | null): number {
    if (!value) {
        return DEFAULT_REPORT_PAGE;
    }

    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
        return DEFAULT_REPORT_PAGE;
    }

    return parsed;
}

function parseTickets(urlParams: URLSearchParams): number[] | undefined {
    if (!urlParams.has('tickets')) {
        return undefined;
    }

    const raw = urlParams.get('tickets');
    if (!raw) {
        return [];
    }

    try {
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) {
            return undefined;
        }

        const ids = parsed.map(toTicketId).filter((id): id is number => id !== null);

        // Ignore legacy name-based URLs (`["GA"]`) instead of treating them as no tickets.
        if (ids.length === 0 && parsed.length > 0) {
            return undefined;
        }

        return ids;
    } catch {
        return undefined;
    }
}

function parseLimit(value: string | null): ReportPageLimit {
    if (value === '25') {
        return 25;
    }

    if (value === '50') {
        return 50;
    }

    if (value === '100') {
        return 100;
    }

    if (value === '250') {
        return 250;
    }

    if (value === 'all') {
        return 'all';
    }

    return DEFAULT_REPORT_LIMIT;
}

export function reportLimitToPageSize(limit: ReportPageLimit, rowCount: number): number {
    if (limit === 'all') {
        return Math.max(rowCount, 1);
    }

    return limit;
}

export function getMaxReportPage(limit: ReportPageLimit, rowCount: number): number {
    const pageSize = reportLimitToPageSize(limit, rowCount);
    return Math.max(1, Math.ceil(rowCount / pageSize));
}

export function clampReportPage(page: number, limit: ReportPageLimit, rowCount: number): number {
    return Math.min(Math.max(page, DEFAULT_REPORT_PAGE), getMaxReportPage(limit, rowCount));
}

function serializeSort(sort: SortingState, reportId: string): string | null {
    const [first] = sort;
    if (!first) {
        return null;
    }

    const config = getReportTableConfig(reportId);
    const column = config ? getReportColumnById(config.columns, first.id) : undefined;
    if (!column || column.sortable === false) {
        return null;
    }

    return `${first.id}:${first.desc ? 'desc' : 'asc'}`;
}

export function parseReportQueryParams(search: string): ReportQueryParams {
    const urlParams = new URLSearchParams(search);
    const report = parseReportId(urlParams.get('report'));

    const range = parseDateRange(urlParams.get('range'));
    const customStart = parseDateParam(urlParams.get('start'));
    const customEnd = parseDateParam(urlParams.get('end'));

    return {
        report,
        view: parseViewId(urlParams.get('view')),
        range: range === 'custom' && (!customStart || !customEnd) ? 'all-time' : range,
        customStart: range === 'custom' ? customStart : undefined,
        customEnd: range === 'custom' ? customEnd : undefined,
        q: urlParams.get('q') ?? '',
        sort: parseSort(urlParams.get('sort'), report),
        page: parsePage(urlParams.get('page')),
        limit: parseLimit(urlParams.get('limit')),
        tickets: parseTickets(urlParams),
    };
}

export function serializeReportQueryParams(params: ReportQueryParams): URLSearchParams {
    const urlParams = new URLSearchParams();

    urlParams.set('report', params.report);

    if (params.view) {
        urlParams.set('view', String(params.view));
    }

    if (params.range !== 'all-time') {
        urlParams.set('range', params.range);
    }

    if (params.range === 'custom' && params.customStart && params.customEnd) {
        urlParams.set('start', params.customStart);
        urlParams.set('end', params.customEnd);
    }

    if (params.q) {
        urlParams.set('q', params.q);
    }

    const sort = serializeSort(params.sort, params.report);
    if (sort) {
        urlParams.set('sort', sort);
    }

    if (params.page !== DEFAULT_REPORT_PAGE && params.limit !== 'all') {
        urlParams.set('page', String(params.page));
    }

    if (params.limit !== DEFAULT_REPORT_LIMIT) {
        urlParams.set('limit', String(params.limit));
    }

    if (params.tickets !== undefined) {
        urlParams.set('tickets', JSON.stringify(params.tickets));
    }

    return urlParams;
}

export function sortingStateToQuerySort(sort: SortingState, reportId: string): SortingState {
    const serialized = serializeSort(sort, reportId);
    if (!serialized) {
        return [];
    }
    return parseSort(serialized, reportId);
}
